<script lang="ts">
  // services
  import { updateLiquorLicense } from "@retailer/services/liquor_license_service";

  // components
  import Icon from "@shared/svelte/Components/Icon.svelte";
  import Modal from "@shared/svelte/Components/Modal.svelte";

  // stores
  import CurrentRetailerStore from "@retailer/svelte/stores/CurrentRetailerStore";
  import CurrentUserStore from "@shared/svelte/Stores/CurrentUserStore";

  // types
  import type { RetailerUser } from "@shared/types/User";

  export let showAddLiquorLicenseModal: boolean;

  const hasLiquorLicense = !!$CurrentRetailerStore?.liquor_license;

  let highlightAck = false;
  let subtext: string;

  function ackChecked(_e: MouseEvent & { currentTarget: HTMLInputElement }): void {
    highlightAck = false;
    $CurrentRetailerStore.liquor_license_ack = !$CurrentRetailerStore.liquor_license_ack;
    $CurrentRetailerStore.license_terms_ack_by_user_id = $CurrentRetailerStore.liquor_license_ack
      ? $CurrentUserStore?.id
      : null;
  }

  function close(): void {
    showAddLiquorLicenseModal = false;
  }

  function getInviterInformation(user: RetailerUser): string {
    const inviterFullName = [user.inviter_first_name, user.inviter_last_name].join(" ").trim();

    if (user.inviter_company_name === "Provi" || (!inviterFullName && !user.inviter_company_name)) {
      return "Please confirm your liquor license is correct.";
    } else if (!!inviterFullName && !user.inviter_company_name) {
      return `This liquor license was provided by ${inviterFullName}.
      Please confirm that this is correct.`;
    } else {
      return `This liquor license was provided by ${inviterFullName}
      from ${user.inviter_company_name}. Please confirm that this is correct.`;
    }
  }

  async function submit(): Promise<any> {
    if ($CurrentRetailerStore.liquor_license && !$CurrentRetailerStore.liquor_license_ack)
      return (highlightAck = true);

    const licenseAttrs = {
      liquorLicense: $CurrentRetailerStore.liquor_license,
      liquorLicenseAck: $CurrentRetailerStore.liquor_license_ack,
      licenseTermsAckByUserId: $CurrentRetailerStore.license_terms_ack_by_user_id,
    };

    await updateLiquorLicense($CurrentRetailerStore.id, licenseAttrs);

    close();
  }

  $: if (!$CurrentRetailerStore?.has_satisfied_liquor_license_requirements && hasLiquorLicense) {
    subtext = getInviterInformation($CurrentUserStore as RetailerUser);
  } else {
    subtext = "";
  }
</script>

<div class="fizz-styles">
  <div class="fizz-text-left">
    <Modal
      bind:show={showAddLiquorLicenseModal}
      fizzModal={true}
      size="medium"
      closeOnBackgroundClick={true}
    >
      <header class="fizz-modal-header">
        <h4 class="fizz-heading-4">
          {#if hasLiquorLicense}
            Confirm your liquor license
          {:else}
            Add your liquor license
          {/if}
        </h4>
        <div class="fizz-modal-toggle fizz-modal-close" on:click={close} on:keypress={close}>
          <Icon type="X" extraClassNames="fizz-text-brand cursor-pointer" height="16" width="16" />
        </div>
      </header>
      <section class="fizz-modal-main fizz-text-medium">
        {#if !!subtext}
          <p class="fizz-stack-24" data-testid="liquor-license-modal-subtext">{subtext}</p>
        {/if}
        <label class="fizz-label fizz-stack-4" for="liquor-license-number">
          Liquor license number
        </label>
        <input
          type="text"
          id="liquor-license-number"
          name="liquor-license-number"
          class="fizz-input-text fizz-full-width fizz-stack-8"
          bind:value={$CurrentRetailerStore.liquor_license}
        />
        <p
          class:fizz-bordered={highlightAck}
          class:fizz-input-error={highlightAck}
          class:fizz-text-error={highlightAck}
          class="fizz-flex-row"
        >
          <input
            type="checkbox"
            id="liquor-license-ack"
            name="liquor-license-ack"
            disabled={!$CurrentRetailerStore.liquor_license}
            on:click={ackChecked}
            bind:checked={$CurrentRetailerStore.liquor_license_ack}
          />
          <span class="fizz-text-small" class:fizz-subdued={!$CurrentRetailerStore.liquor_license}>
            I certify that the above-referenced liquor license is currently valid and duly issued by
            the appropriate governmental agency.
          </span>
        </p>
      </section>
      <footer class="fizz-modal-footer fizz-flex-row fizz-justify-end">
        <button
          class="fizz-button fizz-button-small fizz-modal-toggle fizz-inline-8"
          on:click={close}
        >
          Cancel
        </button>
        <button
          class="fizz-button-secondary fizz-button-small"
          disabled={!$CurrentRetailerStore.liquor_license}
          on:click={submit}
        >
          Save
        </button>
      </footer>
    </Modal>
  </div>
</div>
